import { cva } from "class-variance-authority";

import type { ReactElement } from "react";

const skeletonVariants = cva("block skeleton-sweep", {
  variants: {
    shape: {
      line: "h-3 rounded-[3px]",
      block: "rounded-card",
      circle: "rounded-full",
    },
  },
});

export interface SkeletonProps {
  readonly shape: "line" | "block" | "circle";
  readonly width: string;
  readonly height?: string;
}

const heightOf = (shape: SkeletonProps["shape"], width: string, height: string | undefined) => {
  if (height !== undefined) {
    return { height };
  }
  return shape === "line" ? {} : { height: width };
};

export const Skeleton = ({ shape, width, height }: SkeletonProps): ReactElement => (
  <span
    aria-hidden
    className={skeletonVariants({ shape })}
    style={{ width, ...heightOf(shape, width, height) }}
  />
);

const rowWidths = ["62%", "44%", "76%", "52%", "68%"] as const;

export interface SkeletonRowsProps {
  readonly count: 1 | 2 | 3 | 4 | 5;
}

export const SkeletonRows = ({ count }: SkeletonRowsProps): ReactElement => (
  <div aria-hidden className="flex flex-col">
    {rowWidths.slice(0, count).map((width) => (
      <span
        key={width}
        className="flex min-h-14 items-center gap-3 border-b border-line bg-surface px-5 py-3 coarse:min-h-16"
      >
        <Skeleton shape="circle" width="20px" />
        <span className="flex flex-1 flex-col gap-2">
          <Skeleton shape="line" width={width} />
          <Skeleton shape="line" width="32%" height="10px" />
        </span>
      </span>
    ))}
  </div>
);

const optionWidths = ["64%", "48%", "72%"] as const;

export const SkeletonOptions = (): ReactElement => (
  <span aria-hidden className="flex flex-col">
    {optionWidths.map((width) => (
      <span key={width} className="flex h-9 items-center px-3 coarse:h-11">
        <Skeleton shape="line" width={width} />
      </span>
    ))}
  </span>
);
